import type { AuthCtx, GencowCtx, RealtimeCtx, RealtimeNotifyEvent } from "./context.js";
import { queryRegistry } from "./reactive-query.js";
import type { QueryDef } from "./reactive-query-types.js";
import {
  createRealtimeChannelToken,
  normalizeRealtimeChannelEpoch,
  type RealtimeChannelGrant,
} from "./realtime-channel.js";
import { resolveRealtimeChannelKey } from "./realtime-channel-registry.js";
import {
  createRealtimeQuerySubscriptionToken,
  normalizeRealtimeQuerySubscriptionEpoch,
  type RealtimeQuerySubscriptionGrant,
} from "./realtime-query-subscription.js";
import {
  resolveRealtimeQueryScopeHash,
  resolveRealtimeQuerySubscriptionKey,
} from "./realtime-query-registry.js";

export interface RealtimeFanout {
  /** Dispatch an event to whichever clients should receive it for this runtime. */
  notify(event: RealtimeNotifyEvent): void | Promise<void>;
}

const SUBSCRIPTION_KEY_SEPARATOR = "::";

function createRealtimeEventId(): string {
  const randomUUID = globalThis.crypto?.randomUUID;
  if (typeof randomUUID === "function") return `evt_${randomUUID.call(globalThis.crypto)}`;
  return `evt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
}

function normalizeForStableJson(value: unknown): unknown {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map((item) => normalizeForStableJson(item));
  if (typeof value === "object") {
    const source = value as Record<string, unknown>;
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(source).sort()) {
      const normalized = normalizeForStableJson(source[key]);
      if (normalized !== undefined) sorted[key] = normalized;
    }
    return sorted;
  }
  return value;
}

function isEmptyPlainObject(value: unknown): boolean {
  return !!value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0;
}

/** Stable subscription key for WebSocket / invalidate fanout (also used by crud for query keys). */
export function buildQuerySubscriptionKey(queryKey: string, args?: unknown): string {
  const normalized = normalizeForStableJson(args);
  if (normalized === undefined || isEmptyPlainObject(normalized)) return queryKey;
  return `${queryKey}${SUBSCRIPTION_KEY_SEPARATOR}${JSON.stringify(normalized)}`;
}

export function subscriptionKeyMatchesQueryKey(subscriptionKey: string, queryKey: string): boolean {
  return (
    subscriptionKey === queryKey || subscriptionKey.startsWith(`${queryKey}${SUBSCRIPTION_KEY_SEPARATOR}`)
  );
}

/** Minimal procedure.query shape used by {@link buildRealtimeCtx} refresh flush. */
export interface ProcedureQueryRefreshDef {
  handler: (context: GencowCtx, input: unknown) => Promise<unknown>;
  inputSchema?: unknown;
}

/**
 * mutation 실행 시점에 생성되는 RealtimeCtx.
 * emit(): 데이터를 직접 push (초고빈도 mutation용).
 * refresh(): queryKey를 pending 큐에 추가, mutation 완료 후 서버가 query re-run하여 push.
 *
 * 💡 Batching: 같은 queryKey에 대한 emit이 50ms 내에 여러 번 호출되면
 * 마지막 데이터만 push하여 불필요한 전송을 방지합니다.
 *
 * ⚠️ 매 mutation 호출마다 새로 생성해야 합니다 (debounce timer가 mutation scope에 격리).
 *
 * @param options.fanout        Runtime-specific fanout adapter.
 * @param options.queryMap      legacy `query()` registry — refresh()에서 query handler를 찾아 re-run.
 * @param options.getProcedureQuery  `procedure.query` lookup — arg-less queries re-run; arg queries invalidate.
 * @param options.buildCtxForRefresh  refresh 시 query handler에 전달할 ctx 생성 함수.
 */
export function buildRealtimeCtx(options: {
  fanout: RealtimeFanout;
  queryMap?: Map<string, QueryDef<any, any>>;
  getProcedureQuery?: (key: string) => ProcedureQueryRefreshDef | undefined;
  buildCtxForRefresh?: () => GencowCtx;
  appName?: string;
  channelTokenSecret?: string;
  auth?: AuthCtx;
  deferFanout?: boolean;
}): RealtimeCtx & {
  _hasEmitted: boolean;
  _pendingRefresh: string[];
  _flushRefresh: () => Promise<void>;
  _flushRealtimeEvents: () => Promise<void>;
} {
  const pendingEmits = new Map<string, { data: unknown; timer: ReturnType<typeof setTimeout> }>();
  const pendingEvents: RealtimeNotifyEvent[] = [];
  const _pendingRefresh: string[] = [];
  let _hasEmitted = false;

  function dispatchEvent(event: RealtimeNotifyEvent): void {
    if (options.deferFanout) {
      pendingEvents.push(event);
      return;
    }
    void options.fanout.notify(event);
  }

  return {
    emit(queryKey: string, data: unknown) {
      _hasEmitted = true;
      if (options.deferFanout) {
        pendingEvents.push({ type: "emit", queryKey, data });
        return;
      }
      // 기존 pending timer가 있으면 취소 (debounce)
      const existing = pendingEmits.get(queryKey);
      if (existing) clearTimeout(existing.timer);

      const timer = setTimeout(() => {
        pendingEmits.delete(queryKey);
        dispatchEvent({ type: "emit", queryKey, data });
      }, 50); // 50ms batch window

      pendingEmits.set(queryKey, { data, timer });
    },

    invalidate(queryKey: string | string[]) {
      _hasEmitted = true;
      const queryKeys = Array.isArray(queryKey) ? [...new Set(queryKey)] : [queryKey];
      dispatchEvent({ type: "invalidate", queryKeys });
    },

    refresh(queryKey: string) {
      _hasEmitted = true; // 경고 억제
      if (!_pendingRefresh.includes(queryKey)) {
        _pendingRefresh.push(queryKey);
      }
    },

    channel(channel: string, args?: unknown) {
      const channelKey = resolveRealtimeChannelKey({ channel, args });
      if (!channelKey) throw new Error(`Realtime channel "${channel}" resolved to an invalid channelKey`);
      return {
        authorize(authOptions?: {
          ttlSeconds?: number;
          nowMs?: number;
          nonce?: string;
          epoch?: string | number;
        }): RealtimeChannelGrant {
          if (!options.appName || !options.channelTokenSecret || !options.auth) {
            throw new Error("Authorized realtime channel token signer is not configured");
          }
          const user = options.auth.requireAuth();
          const nowMs = authOptions?.nowMs ?? Date.now();
          const rawTtl = authOptions?.ttlSeconds;
          const ttlSeconds = Number.isFinite(rawTtl) && rawTtl! > 0 ? Math.min(rawTtl!, 3600) : 300;
          const epoch = normalizeRealtimeChannelEpoch(authOptions?.epoch);
          return {
            token: createRealtimeChannelToken({
              appName: options.appName,
              channel,
              channelKey,
              args,
              userId: user.id,
              secret: options.channelTokenSecret,
              ttlSeconds,
              nowMs,
              nonce: authOptions?.nonce,
              epoch,
            }),
            channel,
            channelKey,
            userId: user.id,
            expiresAt: nowMs + Math.floor(ttlSeconds * 1000),
            ...(epoch ? { epoch } : {}),
          };
        },
        publish(data: unknown, publishOptions?: { eventId?: string }) {
          _hasEmitted = true;
          dispatchEvent({
            type: "channel_publish",
            channelKey,
            data,
            eventId: publishOptions?.eventId ?? createRealtimeEventId(),
          });
        },
        invalidate() {
          _hasEmitted = true;
          dispatchEvent({ type: "channel_invalidate", channelKey });
        },
        revoke(revokeOptions?: { epoch?: string | number; reason?: string }) {
          _hasEmitted = true;
          const epoch = normalizeRealtimeChannelEpoch(revokeOptions?.epoch);
          const reason =
            typeof revokeOptions?.reason === "string" && revokeOptions.reason.length > 0
              ? revokeOptions.reason
              : undefined;
          dispatchEvent({
            type: "channel_revoke",
            channelKey,
            ...(epoch ? { epoch } : {}),
            ...(reason ? { reason } : {}),
          });
        },
      };
    },

    query(query: string, args?: unknown) {
      const subscriptionKey = resolveRealtimeQuerySubscriptionKey({ query, args });
      if (!subscriptionKey)
        throw new Error(`Realtime query "${query}" resolved to an invalid subscriptionKey`);
      const scopeHash = resolveRealtimeQueryScopeHash({ query, args, subscriptionKey });
      return {
        authorize(authOptions?: {
          ttlSeconds?: number;
          nowMs?: number;
          nonce?: string;
          epoch?: string | number;
        }): RealtimeQuerySubscriptionGrant {
          if (!options.appName || !options.channelTokenSecret || !options.auth) {
            throw new Error("Authorized realtime query token signer is not configured");
          }
          const user = options.auth.requireAuth();
          const nowMs = authOptions?.nowMs ?? Date.now();
          const rawTtl = authOptions?.ttlSeconds;
          const ttlSeconds = Number.isFinite(rawTtl) && rawTtl! > 0 ? Math.min(rawTtl!, 3600) : 300;
          const epoch = normalizeRealtimeQuerySubscriptionEpoch(authOptions?.epoch);
          return {
            token: createRealtimeQuerySubscriptionToken({
              appName: options.appName,
              query,
              subscriptionKey,
              args,
              scopeHash,
              policy: "authorized",
              userId: user.id,
              secret: options.channelTokenSecret,
              ttlSeconds,
              nowMs,
              nonce: authOptions?.nonce,
              epoch,
            }),
            query,
            subscriptionKey,
            policy: "authorized",
            userId: user.id,
            expiresAt: nowMs + Math.floor(ttlSeconds * 1000),
            ...(epoch ? { epoch } : {}),
          };
        },
        invalidate(invalidateOptions?: { eventId?: string }) {
          _hasEmitted = true;
          dispatchEvent({
            type: "query_invalidate",
            query,
            subscriptionKey,
            scopeHash,
            eventId: invalidateOptions?.eventId ?? createRealtimeEventId(),
          });
        },
        revoke(revokeOptions?: { epoch?: string | number; reason?: string }) {
          _hasEmitted = true;
          const epoch = normalizeRealtimeQuerySubscriptionEpoch(revokeOptions?.epoch);
          const reason =
            typeof revokeOptions?.reason === "string" && revokeOptions.reason.length > 0
              ? revokeOptions.reason
              : undefined;
          dispatchEvent({
            type: "query_revoke",
            query,
            subscriptionKey,
            scopeHash,
            ...(epoch ? { epoch } : {}),
            ...(reason ? { reason } : {}),
          });
        },
      };
    },

    invalidateScoped(input: {
      query: string;
      args?: unknown;
      subscriptionKey?: string;
      scopeHash?: string;
      eventId?: string;
    }) {
      _hasEmitted = true;
      const subscriptionKey =
        input.subscriptionKey ??
        resolveRealtimeQuerySubscriptionKey({ query: input.query, args: input.args });
      if (!subscriptionKey)
        throw new Error(`Realtime query "${input.query}" resolved to an invalid subscriptionKey`);
      const scopeHash =
        input.scopeHash ??
        resolveRealtimeQueryScopeHash({
          query: input.query,
          args: input.args,
          subscriptionKey,
        });
      dispatchEvent({
        type: "query_invalidate",
        query: input.query,
        subscriptionKey,
        scopeHash,
        eventId: input.eventId ?? createRealtimeEventId(),
      });
    },

    get _hasEmitted() {
      return _hasEmitted;
    },
    get _pendingRefresh() {
      return [..._pendingRefresh];
    },

    async _flushRefresh() {
      if (_pendingRefresh.length === 0) return;

      // queryMap이 없으면 refresh 동작 불가 (로그 경고)
      const qMap = options.queryMap ?? queryRegistry;

      for (const key of _pendingRefresh) {
        const legacyDef = qMap.get(key);
        const procedureDef = legacyDef ? undefined : options.getProcedureQuery?.(key);

        if (!legacyDef && !procedureDef) {
          console.warn(`[gencow] refresh("${key}"): query not found in registry. Skipping.`);
          continue;
        }

        // procedure.query with input cannot re-run without subscriber args — invalidate instead.
        if (procedureDef?.inputSchema) {
          options.fanout.notify({ type: "invalidate", queryKeys: [key] });
          continue;
        }

        try {
          // refresh용 ctx 생성 (mutation ctx와 동일한 DB/auth 스코프)
          if (!options.buildCtxForRefresh) {
            console.warn(
              `[gencow] ⚠️  refresh("${key}"): buildCtxForRefresh not provided. ` +
                `Query handler will receive an empty ctx — ctx.db will be undefined. ` +
                `This is a framework configuration error. ` +
                `💡 Ensure buildRealtimeCtx() receives a buildCtxForRefresh callback.`,
            );
          }
          const refreshCtx = options.buildCtxForRefresh?.() ?? ({} as GencowCtx);
          const result = legacyDef
            ? await legacyDef.handler(refreshCtx, {})
            : await procedureDef!.handler(refreshCtx, {});

          // emit과 동일한 경로로 push
          dispatchEvent({ type: "emit", queryKey: key, data: result });
        } catch (e) {
          console.warn(`[gencow] refresh("${key}") failed:`, e instanceof Error ? e.message : e);
        }
      }
      _pendingRefresh.length = 0;
    },

    async _flushRealtimeEvents() {
      while (pendingEvents.length > 0) {
        const event = pendingEvents.shift()!;
        await options.fanout.notify(event);
      }
    },
  };
}
